import { StatusGlobe } from "@/components/globe/status-globe";
import {
  BgGlow,
  type HealthComponent,
  HealthPanel,
  Kicker,
  KpiTile,
  makeSpark,
} from "@/components/iris";
import { useMultiEnvKpis, useMultiEnvStatus } from "@/hooks/use-multi-env";
import { useOverviewComponents } from "@/hooks/use-overview";
import type { ComponentHealth, Env, EnvKpis } from "@/lib/types";
import { Activity, AlertTriangle, Database, Radio, Timer, TrendingUp } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { deriveComponentHealth } from "./derive-health";
import { formatKpiValue } from "./kpi-format";

// ─── Map API component status to iris HealthPanel variant ───

function mapStatusToVariant(status: string): HealthComponent["variant"] {
  switch (status) {
    case "up":
      return "ok";
    case "degraded":
      return "warn";
    case "error":
      return "err";
    case "down":
      return "down";
    default:
      return "warn";
  }
}

function mapStatusToState(status: string): string {
  switch (status) {
    case "up":
      return "OK";
    case "degraded":
      return "WARN";
    case "error":
      return "ERR";
    case "down":
      return "DOWN";
    default:
      return "WARN";
  }
}

function toHealthComponents(components: ComponentHealth[]): HealthComponent[] {
  return components.map((c) => ({
    name: c.name,
    variant: mapStatusToVariant(c.status),
    state: mapStatusToState(c.status),
  }));
}

// ─── KPI Config ───

const KPI_CONFIG: {
  key: keyof EnvKpis;
  label: string;
  icon: LucideIcon;
  seed: number;
}[] = [
  { key: "groups", label: "Groups", icon: Activity, seed: 1 },
  { key: "entities", label: "Entities", icon: TrendingUp, seed: 2 },
  { key: "ingestion_rate", label: "Ingestion rate", icon: Timer, seed: 3 },
  { key: "active_alerts", label: "Active alerts", icon: AlertTriangle, seed: 4 },
  { key: "scraper_coverage", label: "Scraper coverage", icon: Radio, seed: 5 },
  { key: "bucket_backlog", label: "Bucket backlog", icon: Database, seed: 6 },
];

function getKpiUnit(kpis: EnvKpis | null, key: keyof EnvKpis): string | undefined {
  return kpis?.[key]?.unit || undefined;
}

function getTone(env: Env, key: keyof EnvKpis): "cyan" | "purple" | "orange" {
  if (key === "active_alerts") return "orange";
  return env === "dev" ? "cyan" : "purple";
}

// ─── Env KPI Section ───

function EnvKpiSection({ env, kpis }: { env: Env; kpis: EnvKpis | null }) {
  return (
    <div>
      <Kicker variant={env === "dev" ? "cyan" : "muted"} style={{ marginBottom: 12 }}>
        {env === "dev" ? "DEVELOPMENT" : "PRODUCTION"}
      </Kicker>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 16,
        }}
      >
        {KPI_CONFIG.map(({ key, label, icon, seed }) => {
          const unit = getKpiUnit(kpis, key);
          return (
            <KpiTile
              key={key}
              env={env}
              label={label}
              value={formatKpiValue(kpis, key)}
              {...(unit != null ? { unit } : {})}
              icon={icon}
              tone={getTone(env, key)}
              spark={makeSpark(40, seed + (env === "prod" ? 6 : 0))}
            />
          );
        })}
      </div>
    </div>
  );
}

// ─── Main Hero ───

export function DualGlobeHero() {
  const { data: multiStatus } = useMultiEnvStatus();
  const { data: multiKpis } = useMultiEnvKpis();
  const { data: components } = useOverviewComponents();

  const prodComponents = multiStatus?.prod?.components ?? components ?? [];
  const devComponents = multiStatus?.dev?.components ?? [];

  const prodHealth = deriveComponentHealth(prodComponents);
  const devHealth = deriveComponentHealth(devComponents);

  const prodKpis = multiKpis?.prod && !("error" in multiKpis.prod) ? multiKpis.prod : null;
  const devKpis = multiKpis?.dev && !("error" in multiKpis.dev) ? multiKpis.dev : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--iris-s-7)" }}>
      {/* Hero Row: Health Panel -> Globe -> Divider -> Globe -> Health Panel */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto auto auto 1fr",
          alignItems: "center",
          gap: 16,
          position: "relative",
        }}
      >
        {/* Background glows behind the globes */}
        <BgGlow
          color="var(--iris-env-dev)"
          size={500}
          opacity={0.12}
          style={{
            position: "absolute",
            left: "25%",
            top: "50%",
            transform: "translate(-50%, -50%)",
          }}
        />
        <BgGlow
          color="var(--iris-env-prod)"
          size={500}
          opacity={0.12}
          style={{
            position: "absolute",
            left: "75%",
            top: "50%",
            transform: "translate(-50%, -50%)",
          }}
        />

        {/* DEV Health Panel */}
        <div style={{ justifySelf: "end" }}>
          <HealthPanel env="DEV" components={toHealthComponents(devComponents)} />
        </div>

        {/* DEV Globe */}
        <StatusGlobe status={devHealth} size={280} />

        {/* Center divider */}
        <div
          style={{
            width: 1,
            height: 260,
            background:
              "linear-gradient(to bottom, transparent, rgba(255,255,255,0.10), transparent)",
          }}
        />

        {/* PROD Globe */}
        <StatusGlobe status={prodHealth} size={280} />

        {/* PROD Health Panel */}
        <div style={{ justifySelf: "start" }}>
          <HealthPanel env="PROD" components={toHealthComponents(prodComponents)} />
        </div>
      </div>

      {/* KPI Row: Dev (left) | Prod (right) */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32 }}>
        <EnvKpiSection env="dev" kpis={devKpis} />
        <EnvKpiSection env="prod" kpis={prodKpis} />
      </div>

      {/* Cache freshness footer */}
      {multiKpis?.cached_at && (
        <div className="iris-meta" style={{ textAlign: "center" }}>
          KPIs pre-computed &middot; Updated {new Date(multiKpis.cached_at).toLocaleTimeString()}
        </div>
      )}
    </div>
  );
}
